/**
 * Wallet hooks.
 *
 * `useWalletBalance` — used by the AppShell pill and WalletPage.
 * `useWalletTransactions` — paginated transaction ledger for WalletPage
 *   (superall-shell-02). Accumulates pages in memory so the "Load more"
 *   button works without losing already-fetched rows.
 */

import * as React from "react";

import { PLATFORM_API, apiGetJSON } from "../lib/api";
import { useAuth } from "../providers/AuthProvider";

export interface WalletSummary {
  balance: number;
  currency: string;
}

interface UseWalletBalanceResult {
  data: WalletSummary | null;
  loading: boolean;
  error: Error | null;
}

export function useWalletBalance(): UseWalletBalanceResult {
  const { token } = useAuth();
  const [data, setData] = React.useState<WalletSummary | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!token) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGetJSON<WalletSummary>(`${PLATFORM_API}/wallet/balance`)
      .then((next) => {
        if (cancelled) return;
        setData(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { data, loading, error };
}

// ────────────────────────────────────────────────────────────
// Transaction ledger (superall-shell-02)
// ────────────────────────────────────────────────────────────

export type WalletTxType =
  | "credit"
  | "debit"
  | "refund"
  | "topup"
  | "payment"
  | "earn"
  | "redeem";

export interface WalletTransaction {
  id: string;
  type: WalletTxType;
  amount: number;
  currency: string;
  description: string | null;
  reference: string | null;
  createdAt: string;
}

interface WalletTransactionsPage {
  items: WalletTransaction[];
  /** Opaque cursor for the next page; null when no more pages. */
  cursor: string | null;
}

interface UseWalletTransactionsResult {
  items: WalletTransaction[];
  loading: boolean;
  loadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
}

export function useWalletTransactions(): UseWalletTransactionsResult {
  const { token } = useAuth();
  const [items, setItems] = React.useState<WalletTransaction[]>([]);
  const [cursor, setCursor] = React.useState<string | null | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  // Initial load
  React.useEffect(() => {
    if (!token) {
      setItems([]);
      setCursor(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGetJSON<WalletTransactionsPage>(`${PLATFORM_API}/wallet/transactions`)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.cursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadMore = React.useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    apiGetJSON<WalletTransactionsPage>(
      `${PLATFORM_API}/wallet/transactions?cursor=${encodeURIComponent(cursor)}`,
    )
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.cursor);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore]);

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore: cursor !== null && cursor !== undefined,
    loadMore,
  };
}
